import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { skillService, userService } from '../../services/api';
import CategorySubcategorySelect from '../common/CategorySubcategorySelect';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AdminUserSkills = () => {
  const { id } = useParams();
  const userId = String(id);
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [newSkill, setNewSkill] = useState({ categoryId: '', subcategoryId: '', note: '' });

  const loadSkills = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await skillService.list({ user: userId });
      setSkills(res.data);
    } catch (e) {
      setError('Errore nel caricamento abilità utente');
    } finally {
      setLoading(false);
    }
  };

useEffect(() => { loadSkills(); }, [userId]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await userService.getUser(userId);
        setUser(res.data);
      } catch (e) {}
    };
    loadUser();
  }, [userId]);

  const displayName = user ? ((user.first_name || user.last_name) ? `${user.first_name} ${user.last_name}`.trim() : user.email) : `utente #${userId}`;
  const canDelete = currentUser && (currentUser.role === 'admin' || currentUser.role === 'superadmin' || String(currentUser.id) === String(userId));

  const handleAdd = async () => {
    if (!newSkill.categoryId) { setError('Seleziona una categoria'); return; }
    const duplicate = skills.some(s => String(s.category) === String(newSkill.categoryId) && String(s.subcategory || '') === String(newSkill.subcategoryId || ''));
    if (duplicate) { setError('Abilità duplicata per questo utente'); return; }
    try {
      await skillService.create({
        user: Number(userId),
        category: Number(newSkill.categoryId),
        subcategory: newSkill.subcategoryId ? Number(newSkill.subcategoryId) : null,
        note: newSkill.note || '',
      });
      setNewSkill({ categoryId: '', subcategoryId: '', note: '' });
      setSuccess('Abilità inserita');
      loadSkills();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      setError(detail || 'Errore nell\'inserimento abilità');
    }
  };

  const handleEdit = async (skill) => {
    const note = prompt('Nota abilità', skill.note || '');
    if (note === null) return;
    try {
      await skillService.update(skill.id, { note });
      setSuccess('Abilità aggiornata');
      loadSkills();
    } catch (e) {
      setError('Errore nell\'aggiornamento abilità');
    }
  };

  const handleDelete = async (skill) => {
    if (!window.confirm('Eliminare questa abilità?')) return;
    try {
      await skillService.delete(skill.id);
      setSuccess('Abilità eliminata');
      loadSkills();
    } catch (e) {
      setError('Errore nell\'eliminazione abilità');
    }
  };

  

  return (
    <Container component="main" maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography component="h1" variant="h4" gutterBottom>
          Abilità {displayName}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button
            component={Link}
            to="/dashboard"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Box sx={{ mb: 2 }}>
          <Button variant="contained" color="success" onClick={handleAdd}>Aggiungi abilità</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
          <CategorySubcategorySelect compact value={{ categoryId: newSkill.categoryId, subcategoryId: newSkill.subcategoryId }} onChange={(v) => setNewSkill({ ...newSkill, ...v })} />
          <TextField label="Note" value={newSkill.note} onChange={(e) => setNewSkill({ ...newSkill, note: e.target.value })} fullWidth sx={{ minWidth: { xs: '100%', sm: 280 } }} />
        </Box>

        

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : !isMobile ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Sub Categoria</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.category_label}</TableCell>
                    <TableCell>{s.subcategory_label}</TableCell>
                    <TableCell>{s.note}</TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" color="primary" sx={{ mr: 1 }} onClick={() => handleEdit(s)}>Modifica</Button>
                      {canDelete && (
                        <Button size="small" variant="contained" color="error" onClick={() => handleDelete(s)}>Elimina</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {skills.length > 0 ? (
              skills.map((s) => (
                <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {s.category_label || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {s.subcategory_label || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {s.note || '—'}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="contained" color="primary" onClick={() => handleEdit(s)}>
                      Modifica
                    </Button>
                    {canDelete && (
                      <Button size="small" variant="contained" color="error" onClick={() => handleDelete(s)}>
                        Elimina
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body2" align="center">Nessuna abilità inserita.</Typography>
              </Paper>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminUserSkills;
